<template>
  <grid-page-loading-error
    v-if="error !== null && !loading"
    @retry="init"
  />
  <div
    v-else-if="gridPageTileMedia === null || loading"
    class="_spinner"
  >
    <m-spinner />
  </div>
  <m-content
    v-else
    :padding-top="gridPageTile.hideBorder ? -1 : 0"
    :padding-x="gridPageTile.hideBorder ? -1 : 9"
    :padding-bottom="gridPageTile.hideBorder ? -1 : 6"
    class="grid-page-tile-media"
  >
    <m-dropdown
      v-model:value="showSettings"
      :title="$t('general.actions')"
      block
      :relocate-key="relocateKey"
      @hide="hide"
    >
      <div
        v-if="!hasContent"
        class="_button-container"
      >
        <div class="_button">
          <div class="_icon">
            <m-icon type="file-image" />
          </div>
          <div class="_text">
            {{ $t('gridPage.gridPageTileMedia.uploadImageOrEmbedVideo') }}
          </div>
        </div>
      </div>
      <template #overlay>
        <m-card
          no-padding
          class="_card"
        >
          <m-content
            no-padding
            :padding-x="8"
            class="_header"
          >
            <div class="_tabs">
              <m-tab
                :title="$t('gridPage.gridPageTileMedia.uploadImage')"
                small
                :active="activeTab === 'image'"
                @click="activeTab = 'image'"
              />
              <m-tab
                :title="$t('gridPage.gridPageTileMedia.embedVideoLink')"
                small
                :active="activeTab === 'video'"
                @click="activeTab = 'video'"
              />
            </div>
          </m-content>
          <m-content
            padding-xs
            class="_content"
          >
            <div
              v-if="activeTab === 'image'"
              class="_image-tab"
            >
              <m-btn
                block
                color="secondary"
                @click="triggerImageUpload"
              >
                {{ $t('gridPage.gridPageTileMedia.uploadFile') }}
              </m-btn>
              <input
                ref="imageInput"
                type="file"
                accept="image/*"
                style="display: none"
                @change="handleImageChange"
              >
            </div>
            <div
              v-if="activeTab === 'video'"
              class="_video-tab"
            >
              <m-form-item>
                <m-input
                  v-model:value="videoUrl"
                  :placeholder="$t('gridPage.gridPageTileMedia.pasteVideoLink')"
                  auto-focus
                  :has-error="!validVideoUrl"
                  full-width
                />
              </m-form-item>
              <m-btn
                block
                color="primary"
                :disabled="!validVideoUrl"
                @click="saveVideoUrl"
              >
                {{ $t('gridPage.gridPageTileMedia.embedVideo') }}
              </m-btn>
              <div class="_hint">
                {{ $t('gridPage.gridPageTileMedia.supportedPlatforms') }}
              </div>
            </div>
          </m-content>
        </m-card>
      </template>
    </m-dropdown>

    <div
      v-if="hasContent"
      class="_content-display"
    >
      <img
        v-if="gridPageTileMedia.image"
        class="_media-image"
        :src="gridPageTileMedia.image.getURL"
        :alt="gridPageTileMedia.image.fileName || ''"
        @error="error = $event"
      >
      <iframe
        v-else-if="gridPageTileMedia.videoUrl"
        class="_media-video"
        :src="formattedVideoUrl(gridPageTileMedia.videoUrl)"
        frameborder="0"
        allowfullscreen
        sandbox="allow-presentation allow-storage-access-by-user-activation allow-scripts allow-forms allow-same-origin allow-popups allow-popups-to-escape-sandbox"
      />
    </div>
  </m-content>
</template>

<script setup>
import GridPageLoadingError from '@/components/custom-grid/GridPageLoadingError.vue';
import useGridPage from '@/composables/grid-page/grid-page';
import useInlineEditing from '@/composables/inline-editing';
import useSnackBar from '@/composables/snackbar';
import { computed, onBeforeUnmount, ref, toRef, watch } from 'vue';
import { isEmpty, isNullOrUndefined } from 'shared/lib/object/object';
import { logCatch } from '@/lib/logger/logger';
import { uploadFile } from '@/lib/image/image';

const props = defineProps({
  gridPageTile: { type: Object, required: true },
  readOnly: { type: Boolean, default: false },
  showReplaceMenu: { type: Boolean, default: false },
});

const emit = defineEmits(['close-replace-menu']);

const gridPageSvc = useGridPage();
const inlineEditingSvc = useInlineEditing();

const activeTab = ref('image');
const imageInput = ref(null);
const videoUrl = ref('');
const relocateKey = ref(0);
const showSettings = ref(false);

const autoOpen = inlineEditingSvc.isInlineEditing(props.gridPageTile.uid);
watch(autoOpen, (val) => {
  if (val) {
    showSettings.value = true;
    setTimeout(() => {
      relocateKey.value += 1;
    }, 0);
  }
}, { immediate: true });

const showReplaceMenu = toRef(props, 'showReplaceMenu');
watch(showReplaceMenu, (val) => {
  if (val) {
    showSettings.value = true;
  }
});

const hide = () => {
  inlineEditingSvc.reset();
  emit('close-replace-menu');
};

const gridPageTileMedia = computed(() => {
  const res = gridPageSvc.gridPageTileMedia.value.find((e) => e.tile.uid === props.gridPageTile.uid);
  if (res === undefined) {
    return null;
  }

  return res;
});

const hasContent = computed(() => !isNullOrUndefined(gridPageTileMedia.value.image) || !isEmpty(gridPageTileMedia.value.videoUrl));

// Try to normalize url for parsing by URL constructor
const normalizeUrl = (raw) => {
  if (isEmpty(raw)) return '';
  if (/^https?:\/\//i.test(raw)) return raw;
  return `https://${raw}`;
};

// Extract { platform, id } from supported URLs; returns null if not extractable
const extractVideo = (rawUrl) => {
  try {
    const normalized = normalizeUrl(rawUrl);
    const u = new URL(normalized);
    const host = u.hostname.replace(/^www\./, '');
    // YouTube
    if (host === 'youtu.be') {
      const id = u.pathname.split('/')[1] || '';
      return id ? { platform: 'youtube', id } : null;
    }
    if (host === 'youtube.com' || host === 'm.youtube.com') {
      // watch?v=, embed/:id, shorts/:id
      const vParam = u.searchParams.get('v');
      const pathSegs = u.pathname.split('/').filter(Boolean);
      const byEmbed = pathSegs[0] === 'embed' ? pathSegs[1] : '';
      const byShorts = pathSegs[0] === 'shorts' ? pathSegs[1] : '';
      const id = vParam || byEmbed || byShorts || '';
      return id ? { platform: 'youtube', id } : null;
    }
    // Vimeo
    if (host === 'vimeo.com' || host === 'player.vimeo.com') {
      const segs = u.pathname.split('/').filter(Boolean);
      const id = host === 'player.vimeo.com' && segs[0] === 'video' ? segs[1] : segs[0];
      return id ? { platform: 'vimeo', id } : null;
    }
    // Loom
    if (host === 'loom.com' || host === 'www.loom.com') {
      const segs = u.pathname.split('/').filter(Boolean);
      const id = segs[0] === 'share' || segs[0] === 'embed' ? segs[1] : '';
      return id ? { platform: 'loom', id } : null;
    }
  } catch (e) {
    // fall through
  }
  return null;
};

const validVideoUrl = computed(() => {
  if (videoUrl.value === '') return true;
  return extractVideo(videoUrl.value) !== null;
});

const triggerImageUpload = () => {
  if (props.readOnly) return;
  imageInput.value?.click();
};

const handleImageChange = (event) => {
  const file = event.target.files[0];
  if (!file) {
    return;
  }

  uploadImage(file);
  imageInput.value.value = '';
};

const snackbar = useSnackBar();
const uploadImage = (file) => {
  loading.value = true;
  uploadFile(file)
    .then((data) => {
      const image = () => {
        if (gridPageTileMedia.value.image === null) {
          return { blob: data.fileId, getURL: data.getURL };
        }
        return {
          uid: gridPageTileMedia.value.image.uid,
          blob: data.fileId,
          getURL: data.getURL,
        };
      };
      gridPageSvc.updateGridPageTileMedia({ uid: gridPageTileMedia.value.uid, image: image(), videoUrl: null }, { optimistic: false }).then(() => {
        showSettings.value = false;
      });
    })
    .catch(logCatch(() => {
      snackbar.error();
    }))
    .finally(() => {
      loading.value = false;
    });
};

const saveVideoUrl = () => {
  if (!validVideoUrl.value) return;

  gridPageSvc.updateGridPageTileMedia({ uid: gridPageTileMedia.value.uid, image: null, videoUrl: videoUrl.value }).then(() => {
    showSettings.value = false;
  });
};

const formattedVideoUrl = (url) => {
  if (isEmpty(url)) return '';
  const info = extractVideo(url);
  if (info === null) return '';
  if (info.platform === 'youtube') return `https://www.youtube.com/embed/${info.id}`;
  if (info.platform === 'vimeo') return `https://player.vimeo.com/video/${info.id}`;
  if (info.platform === 'loom') return `https://www.loom.com/embed/${info.id}`;
  return '';
};

watch(showSettings, (open) => {
  if (open) {
    videoUrl.value = gridPageTileMedia.value.videoUrl;
  }
});

onBeforeUnmount(() => {
  inlineEditingSvc.reset();
});

const error = ref(null);
const loading = ref(false);
const init = () => {
  loading.value = true;
  error.value = null;
  gridPageSvc.queryGridPageTileMediaByTile(props.gridPageTile)
    .then(() => {
      videoUrl.value = gridPageTileMedia.value.videoUrl;
    })
    .catch(logCatch((e) => {
      error.value = e;
    }))
    .finally(() => {
      loading.value = false;
    });
};

init();

</script>

<style
  scoped
  lang="scss"
  type="text/scss"
>
._spinner {
  height: var(--viewport-height-100);
  display: flex;
  justify-content: center;
  align-items: center;
}

.grid-page-tile-media {
  overflow: hidden;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;

  ._button-container {
    display: flex;
    width: 100%;
    justify-content: center;
  }

  ._button {
    border-radius: $default-border-radius;
    color: map_get($grey, 'darken-1');
    background: map_get($grey, 'lighten-5');
    padding: 1rem;
    margin: 1.6rem;
    display: flex;
    align-items: center;
    cursor: pointer;

    &:hover {
      background: map_get($grey, 'lighten-3');
    }

    ._icon {
      margin-right: 1.2rem;
    }
  }

  ._content-display {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: flex-start;
    justify-content: center;

    ._media-image {
      display: block;
      width: auto;
      height: auto;
      max-width: 100%;
      max-height: 100%;
      object-fit: contain;
      object-position: top center;
      border-radius: $default-border-radius;
    }

    ._media-video {
      width: 100%;
      height: 100%;
      border: none;
    }
  }
}

._card {
  width: 40rem;
  max-width: 90vw;

  ._header {
    border-bottom: 1px solid $border-color;
  }

  ._hint {
    margin-top: .6rem;
    text-align: center;
    color: $font-color-secondary;
    font-size: $font-size-2;
  }
}
</style>
